import { defaultDarkModeOverride, type Theme } from "@aws-amplify/ui-react";

// Amplify Authenticator themed to the design tokens (016 §5.1, review v2 §2/P1).
// The FULL brand.primary ramp is mapped (not just 10/80/90/100) so Amplify's
// intermediate steps stop falling back to its default cyan; the tabs, links and
// field focus are pinned to brand teal #107169, and the font to DM Sans, so the
// login screen reads as the same product as the app behind it.
const TEAL = "#107169";
// primary.40 — the same ramp, one step light enough to read on the dark
// card. Brand teal itself is only 2.8:1 there, below AA for text and below
// the 3:1 a focus ring or an active-tab underline needs.
const TEAL_ON_DARK = "#5fb3a7";
export const amplifyTheme: Theme = {
  name: "Composo",
  tokens: {
    colors: {
      // Amplify components read `colors.primary`, which DEFAULTS to the `teal`
      // scale (teal-80 = #047d95, its cyan) — NOT `brand.primary`. Override the
      // primary ramp directly so the button/tabs/links all use brand teal.
      primary: {
        10: { value: "#e6f2f0" },
        20: { value: "#c2e0db" },
        40: { value: "#5fb3a7" },
        60: { value: "#2c9384" },
        80: { value: "#138072" },
        90: { value: TEAL },
        100: { value: "#0c5751" },
      },
      brand: {
        primary: {
          10: { value: "#e6f2f0" },
          20: { value: "#c2e0db" },
          40: { value: "#5fb3a7" },
          60: { value: "#2c9384" },
          80: { value: "#138072" },
          90: { value: TEAL },
          100: { value: "#0c5751" },
        },
      },
      font: {
        interactive: { value: TEAL },
      },
      border: {
        focus: { value: TEAL },
      },
    },
    fonts: {
      default: {
        variable: { value: "DMSans, ui-sans-serif, system-ui, sans-serif" },
        static: { value: "DMSans, ui-sans-serif, system-ui, sans-serif" },
      },
    },
    components: {
      authenticator: {
        router: {
          borderColor: { value: "hsl(214 20% 90%)" },
          boxShadow: { value: "0 1px 2px 0 rgb(0 0 0 / 0.05)" },
        },
      },
      tabs: {
        item: {
          color: { value: "#475569" },
          fontWeight: { value: "600" },
          _hover: { color: { value: "#138072" } },
          _active: { color: { value: TEAL }, borderColor: { value: TEAL } },
          _focus: { color: { value: TEAL } },
        },
      },
      fieldcontrol: {
        _focus: { borderColor: { value: TEAL } },
      },
    },
  },
  // Dark-mode overrides. Amplify does NOT apply a dark palette implicitly: a
  // custom theme gets exactly the overrides it declares, so without
  // defaultDarkModeOverride every token we did not pin by hand keeps its LIGHT
  // value under colorMode="dark" — which is how the field text ended up
  // near-black on the dark card (#1544). defaultDarkModeOverride flips the base
  // colour palette (neutrals, font, background, border); it touches `colors`
  // only, so the brand teal pinned in the base tokens carries over untouched —
  // which is why the accents that sit ON the dark card are re-pinned below to
  // TEAL_ON_DARK. Ours is listed second so that where the two collide (the
  // router surface, the tab text) our design-token values win: createTheme
  // emits one CSS block per override at equal specificity, so later wins.
  overrides: [
    defaultDarkModeOverride,
    {
      colorMode: "dark",
      tokens: {
        colors: {
          font: {
            // Amplify flips font.inverse to near-black in dark mode because it
            // assumes the accent lightens with the palette. Ours does not — the
            // primary ramp is pinned to brand teal in both modes — so the text
            // on the teal "Sign in" button has to stay white (5.9:1 on #107169;
            // near-black would be 3.0:1).
            inverse: { value: "{colors.white}" },
            // "Forgot your password?" and the other links.
            interactive: { value: TEAL_ON_DARK },
          },
          border: { focus: { value: TEAL_ON_DARK } },
        },
        components: {
          authenticator: {
            router: {
              backgroundColor: { value: "hsl(216 24% 13%)" },
              borderColor: { value: "hsl(216 16% 25%)" },
              boxShadow: { value: "0 1px 2px 0 rgb(0 0 0 / 0.4)" },
            },
          },
          tabs: {
            item: {
              color: { value: "hsl(216 14% 66%)" },
              // Without these the SELECTED tab renders dimmer than the
              // unselected one (2.8:1 against 6.3:1), so it reads as unselected.
              _hover: { color: { value: TEAL_ON_DARK } },
              _active: {
                color: { value: TEAL_ON_DARK },
                borderColor: { value: TEAL_ON_DARK },
              },
              _focus: { color: { value: TEAL_ON_DARK } },
            },
          },
          fieldcontrol: {
            _focus: { borderColor: { value: TEAL_ON_DARK } },
          },
        },
      },
    },
  ],
};
